import React, { useState, useRef } from "react";
import { Flask, Plus, X, Check, ArrowLeft, Brain, Lightning } from "@phosphor-icons/react";
import { T } from "../themes";
import { llmFetch, LLM_MODEL_LIGHT, AI_SAFETY_LAW } from "../llm";
import Btn from "../components/Btn";
import Spinner from "../components/Spinner";
import BottomNav from "../components/BottomNav";
import AppHeader from "../components/AppHeader";

export default function SupplementsScreen({
  screen,
  setScreen,
  shell,
  phone,
  OfflineBanner,
  hasTargets,
  persona,
  darkMode, setDarkMode,
  session,
  suppStack,
  suppTakenToday,
  showSuppAdd,
  setShowSuppAdd,
  suppAddStep,
  setSuppAddStep,
  suppForm,
  setSuppForm,
  suppSearch,
  setSuppSearch,
  suppSearchRes,
  setSuppSearchRes,
  suppSearching,
  suppLoading,
  setSuppLoading,
  suppScheduleLoad,
  suppScheduleMsg,
  suppDbDebounceRef,
  SUPP_TIMINGS,
  addSupplement,
  toggleSuppTaken,
  deleteSupplement,
  searchSuppDB,
  runSuppSchedulePlanner,
  weight,
}) {
  const totalToday  = suppStack.length;
  const takenToday  = suppStack.filter(s => suppTakenToday.has(s.id)).length;
  const grouped = SUPP_TIMINGS.reduce((acc, t) => {
    const items = suppStack.filter(s => s.timing === t);
    if (items.length > 0) acc[t] = items;
    return acc;
  }, {});

  // Tap-to-confirm delete state
  const [pendingDelete, setPendingDelete] = useState(null);
  const pendingTimer = useRef(null);
  function requestDelete(id) {
    if (pendingDelete === id) {
      clearTimeout(pendingTimer.current);
      setPendingDelete(null);
      deleteSupplement(id);
    } else {
      clearTimeout(pendingTimer.current);
      setPendingDelete(id);
      pendingTimer.current = setTimeout(() => setPendingDelete(null), 3000);
    }
  }

  return (
    <div style={shell}><div style={phone}>
      {OfflineBanner}

      {/* ── Header ─────────────────────────────────────────────────── */}
      <AppHeader
        title="Supplements"
        subtitle="Stack & scheduling"
        persona={persona}
        darkMode={darkMode}
        onToggleDarkMode={()=>setDarkMode(d=>!d)}
        currentApp="nutri"
        onAvatarPress={()=>setScreen&&setScreen("settings")}
        username={session?.user?.email}
      />

      <div style={{padding:"14px 16px",overflowY:"auto",flex:1,paddingBottom:24,background:T.pageBg}}>

        {/* ── Today's Progress + Adherence ring — dark header card ─── */}
        {totalToday > 0 && (()=>{
          const pct = totalToday > 0 ? Math.round((takenToday/totalToday)*100) : 0;
          const _hour = new Date().getHours();
          const allDone = takenToday === totalToday;
          const noneTaken = takenToday === 0;
          const preNoon = _hour < 12;
          // Derive friendly status + colour without punishing users before midday
          let statusLabel, statusColor, ringLabel;
          if (allDone) {
            statusLabel = "All done"; statusColor = T.accent; ringLabel = "100%";
          } else if (noneTaken && preNoon) {
            statusLabel = "Ready for today"; statusColor = T.inkMuted; ringLabel = "—";
          } else if (noneTaken) {
            statusLabel = "Behind schedule"; statusColor = T.red||"#ef4444"; ringLabel = "0%";
          } else {
            statusLabel = `${takenToday} of ${totalToday} taken`;
            statusColor = pct >= 50 ? T.accent : (T.yellow||"#f59e0b");
            ringLabel = `${pct}%`;
          }
          return (
            <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,overflow:"hidden",marginBottom:14}}>
              {/* Dark header */}
              <div style={{backgroundColor:T.tileBg,padding:"10px 16px",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                <span style={{color:T.tileAccent,fontSize:11,fontWeight:700,fontFamily:T.fontSans,letterSpacing:"0.5px"}}>Today's progress</span>
              </div>
              {/* Body */}
              <div style={{padding:"12px 16px"}}>
                <div style={{height:4,background:T.surfaceLight,borderRadius:2,overflow:"hidden",marginBottom:12}}>
                  <div style={{height:"100%",background:T.accent,transition:"width 0.4s",borderRadius:2,
                    width:`${totalToday>0?(takenToday/totalToday)*100:0}%`}}/>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:36,height:36,borderRadius:"50%",border:`3px solid ${statusColor}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <span style={{fontSize:11,fontFamily:T.fontDisplay,fontWeight:800,color:statusColor}}>{ringLabel}</span>
                  </div>
                  <div>
                    <p style={{margin:0,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,color:T.inkMuted,letterSpacing:"0.5px"}}>Today's adherence</p>
                    <p style={{margin:"2px 0 0",fontSize:10,fontFamily:T.fontDisplay,fontWeight:600,color:statusColor}}>{statusLabel}</p>
                  </div>
                </div>
                {(()=>{
                  const _hour=new Date().getHours();
                  const _mDue=suppStack.filter(s=>s.timing?.toLowerCase().includes("morning")&&!suppTakenToday.has(s.id));
                  const _wDue=suppStack.filter(s=>s.timing?.toLowerCase().includes("meal")&&!suppTakenToday.has(s.id));
                  const _bDue=suppStack.filter(s=>(s.timing?.toLowerCase().includes("bed")||s.timing?.toLowerCase().includes("night"))&&!suppTakenToday.has(s.id));
                  const _nudge=_hour<10&&_mDue.length>0
                    ?`${_mDue[0].name} is due now`
                    :_hour>=12&&_hour<20&&_wDue.length>0
                    ?`Take ${_wDue[0].name} with your meal`
                    :_hour>=20&&_bDue.length>0
                    ?`${_bDue[0].name} before bed tonight`
                    :suppStack.length>0&&suppStack.every(s=>suppTakenToday.has(s.id))
                    ?"All supplements taken today ✓"
                    :null;
                  if(!_nudge)return null;
                  return(
                    <div style={{backgroundColor:T.aiTagBg,border:`1px solid ${T.cardBorder}`,borderRadius:12,padding:"10px 14px",marginTop:12,display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:16}}>💊</span>
                      <span style={{fontFamily:T.fontSans,fontSize:13,color:T.inkDark,fontWeight:500}}>{_nudge}</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })()}

        {/* ── AI Schedule Planner ────────────────────────────────────── */}
        {suppStack.length > 0 && (
          <div style={{marginBottom:14}}>
            <div style={{background:T.tileBg,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,overflow:"hidden"}}>
              <button onClick={runSuppSchedulePlanner} disabled={suppScheduleLoad}
                style={{width:"100%",background:"transparent",border:"none",
                  padding:"14px 16px",cursor:suppScheduleLoad?"default":"pointer",
                  display:"flex",alignItems:"center",justifyContent:"space-between",
                  opacity:suppScheduleLoad?0.6:1}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:18}}>🧠</span>
                  <div style={{textAlign:"left"}}>
                    <div style={{fontFamily:T.fontDisplay,fontSize:15,fontWeight:700,color:T.tileAccent,lineHeight:1.1}}>
                      {suppScheduleLoad?"Planning your schedule…":"AI plan my schedule"}
                    </div>
                    <div style={{fontFamily:T.fontSans,fontSize:12,color:"rgba(255,255,255,0.5)",marginTop:2}}>Personalised supplement timing</div>
                  </div>
                </div>
                {!suppScheduleLoad&&<span style={{color:"rgba(255,255,255,0.4)",fontSize:18,lineHeight:1}}>›</span>}
                {suppScheduleLoad&&<Spinner size={14}/>}
              </button>
              {suppScheduleMsg&&<div style={{padding:"0 16px 10px",borderTop:"1px solid rgba(255,255,255,0.08)"}}><p style={{color:T.tileAccent,fontSize:11,fontFamily:T.fontDisplay,fontWeight:600,margin:"8px 0 0",textAlign:"center"}}>{suppScheduleMsg}</p></div>}
              <p style={{color:"rgba(255,255,255,0.3)",fontSize:9,fontFamily:T.fontSans,margin:"0 16px 10px",textAlign:"center",lineHeight:1.4}}>AI suggestion — consult a healthcare professional before use.</p>
            </div>
          </div>
        )}

        {/* ── Add Supplement Button ──────────────────────────────────── */}
        <button
          onClick={()=>{setShowSuppAdd(true);setSuppForm({name:"",dose:"",timing:"Morning",notes:""});setSuppSearch("");setSuppSearchRes([]);}}
          style={{width:"100%",background:T.orange,border:"none",borderRadius:T.radiusSm,
            padding:14,cursor:"pointer",marginBottom:14,
            display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          <Plus size={16} weight="bold" color="#fff"/>
          <span style={{color:"#fff",fontSize:15,fontFamily:T.fontDisplay,fontWeight:800}}>Add supplement to stack</span>
        </button>

        {suppStack.length === 0 && (
          <div style={{textAlign:"center",padding:"24px 20px",border:`1px solid ${T.cardBorder}`,background:T.card,borderRadius:18,marginBottom:14}}>
            <Flask size={28} weight="duotone" color={T.inkMuted}/>
            <p style={{color:T.inkMuted,fontSize:11,margin:"10px 0 0",fontFamily:T.fontDisplay,fontWeight:500}}>Your stack is empty. Add your first supplement above.</p>
          </div>
        )}

        {/* ── Supplement Groups by Timing ────────────────────────────── */}
        {Object.entries(grouped).map(([timing, items]) => (
          <div key={timing} style={{marginBottom:12}}>
            {/* Timing section label */}
            <div style={{borderTop:`1px solid ${T.cardBorder}`,paddingTop:8,marginTop:16,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{color:T.inkDark,fontSize:12,fontFamily:T.fontDisplay,fontWeight:700}}>{timing}</span>
              <span style={{backgroundColor:T.surfaceMid,color:T.inkMuted,borderRadius:T.radiusSm,padding:"2px 8px",fontSize:11,fontFamily:T.fontDisplay,fontWeight:600}}>
                {items.filter(s=>suppTakenToday.has(s.id)).length}/{items.length} taken
              </span>
            </div>
            {/* Items card */}
            <div style={{backgroundColor:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,overflow:"hidden"}}>
              {items.map((s,i) => {
                const taken = suppTakenToday.has(s.id);
                return (
                  <div key={s.id} style={{padding:"14px 16px",borderBottom:i<items.length-1?`1px solid ${T.surfaceLight}`:"none",
                    display:"flex",alignItems:"center",gap:10,
                    background:taken?`${T.accent}18`:"transparent"}}>
                    <button onClick={()=>toggleSuppTaken(s)} style={{
                      width:44,height:44,flexShrink:0,
                      background:"transparent",border:"none",
                      cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
                      <div style={{
                        width:22,height:22,borderRadius:6,
                        background:taken?T.accent:"transparent",
                        border:`2px solid ${taken?T.accent:T.inkFaint}`,
                        display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {taken&&<Check size={13} weight="bold" color="#fff"/>}
                      </div>
                    </button>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{color:taken?T.inkMuted:T.inkDark,fontSize:18,fontFamily:T.fontDisplay,fontWeight:800,margin:0,
                        textDecoration:taken?"line-through":"none"}}>{s.name}</p>
                      <p style={{color:T.inkMuted,fontSize:11,fontFamily:T.fontDisplay,fontWeight:500,margin:"2px 0 0"}}>
                        {[s.dose,s.notes].filter(Boolean).join(" \u00b7 ")||"No dose specified"}
                      </p>
                    </div>
                    <button onClick={()=>requestDelete(s.id)}
                      style={{
                        marginLeft:16,flexShrink:0,cursor:"pointer",border:"none",
                        ...(pendingDelete===s.id
                          ? {background:`${T.red||"#ef4444"}18`,border:`1px solid ${T.red||"#ef4444"}`,borderRadius:8,
                              padding:"10px 14px",color:T.red||"#ef4444",fontSize:11,fontFamily:T.fontSans,fontWeight:600,lineHeight:1}
                          : {background:"none",color:T.inkMuted,width:44,height:44,
                              display:"flex",alignItems:"center",justifyContent:"center",padding:0,borderRadius:6,margin:"-6px -6px -6px 10px"})
                      }}>
                      {pendingDelete===s.id ? "Delete?" : <X size={16} weight="bold"/>}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

      </div>

      {/* ── Add Supplement Modal ─────────────────────────────────────── */}
      {showSuppAdd&&(
        <div style={{position:"fixed",inset:0,background:T.backdrop,display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:400}}>
          <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:"24px 24px 0 0",width:"100%",maxWidth:480,maxHeight:"85vh",display:"flex",flexDirection:"column"}}>
            <div style={{padding:"16px 16px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0,borderBottom:`1px solid ${T.cardBorder}`}}>
              <p style={{color:T.inkDark,fontSize:14,fontFamily:T.fontDisplay,fontWeight:800,margin:0}}>Add supplement</p>
              <button onClick={()=>setShowSuppAdd(false)} style={{background:"none",border:"none",cursor:"pointer",color:T.inkMuted}}><X size={18} weight="bold"/></button>
            </div>
            {/* Step 1: Search */}
            {suppAddStep==="search"&&(
              <div style={{padding:"14px 16px",flex:1,display:"flex",flexDirection:"column",gap:10}}>
                <p style={{color:T.inkMuted,fontSize:11,fontFamily:T.fontDisplay,fontWeight:500,margin:0}}>Search the supplement database, or type a name to add manually.</p>
                <div style={{position:"relative"}}>
                  <input autoFocus value={suppSearch}
                    onChange={e=>{
                      const v=e.target.value; setSuppSearch(v);
                      clearTimeout(suppDbDebounceRef.current);
                      if(v.length>=2) suppDbDebounceRef.current=setTimeout(()=>searchSuppDB(v),300);
                      else setSuppSearchRes([]);
                    }}
                    onKeyDown={e=>e.key==="Escape"&&setSuppSearchRes([])}
                    placeholder="Vitamin D, Creatine, Magnesium..."
                    style={{width:"100%",background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                      padding:"11px 12px",color:T.inkDark,fontSize:14,
                      fontFamily:T.fontDisplay,fontWeight:600,outline:"none",boxSizing:"border-box"}}/>
                  {suppSearching&&<div style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)"}}><Spinner size={14}/></div>}
                </div>
                {suppSearchRes.length>0&&(
                  <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,overflowY:"auto",maxHeight:300}}>
                    {suppSearchRes.map((row,i)=>(
                      <button key={i} onClick={()=>{
                        setSuppForm(f=>({...f,name:row.name,dose:row.serving_label||""}));
                        setSuppSearch(""); setSuppSearchRes([]);
                        setSuppAddStep("details");
                      }}
                        style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",
                          borderBottom:i<suppSearchRes.length-1?`1px solid ${T.surfaceLight}`:"none",
                          padding:"11px 14px",cursor:"pointer"}}>
                        <p style={{color:T.inkDark,fontSize:12,fontFamily:T.fontDisplay,fontWeight:800,margin:0}}>{row.name}</p>
                        {row.serving_label&&<p style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontDisplay,fontWeight:500,margin:"2px 0 0"}}>{row.serving_label}</p>}
                        {row.category&&<p style={{color:T.accent,fontSize:9,fontFamily:T.fontDisplay,fontWeight:700,margin:"1px 0 0"}}>{row.category}</p>}
                      </button>
                    ))}
                  </div>
                )}
                {suppSearch.length>=2&&!suppSearching&&suppSearchRes.length===0&&(
                  <button onClick={()=>{setSuppForm(f=>({...f,name:suppSearch,dose:""}));setSuppAddStep("details");}}
                    style={{background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,padding:"11px 14px",cursor:"pointer",textAlign:"left",width:"100%"}}>
                    <p style={{color:T.inkDark,fontSize:12,fontFamily:T.fontDisplay,fontWeight:800,margin:0}}>Add "{suppSearch}" manually</p>
                    <p style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontDisplay,fontWeight:500,margin:"2px 0 0"}}>Not in database -- add as custom supplement</p>
                  </button>
                )}
              </div>
            )}
            {/* Step 2: Details */}
            {suppAddStep==="details"&&(
              <>
              <div style={{overflowY:"auto",flex:1,padding:"14px 16px"}}>
                <button onClick={()=>setSuppAddStep("search")} style={{background:"none",border:"none",cursor:"pointer",color:T.accent,padding:0,marginBottom:12,display:"flex",alignItems:"center",gap:5,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700}}>
                  <ArrowLeft size={12} weight="bold"/> Back to search
                </button>
                <div style={{background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,padding:"10px 12px",marginBottom:14}}>
                  <p style={{color:T.accent,fontSize:13,fontFamily:T.fontDisplay,fontWeight:800,margin:0}}>{suppForm.name}</p>
                </div>
                <div style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                    <label style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,letterSpacing:"1px"}}>Dose / Strength</label>
                    <button onClick={async()=>{
                      if(!suppForm.name) return;
                      setSuppLoading(true);
                      try {
                        const raw = await llmFetch({
                          model:LLM_MODEL_LIGHT, max_tokens:80, temperature:0.3,
                          messages:[{role:"system",content:`You are a clinical sports nutrition specialist. ${AI_SAFETY_LAW} Never recommend doses for prescription medications.`},{role:"user",content:`For a person weighing ${weight}kg, what is the standard recommended dose for: ${suppForm.name}? Reply in ONLY this format: "Xmg" or "X capsule(s)" or "Xg". No explanation.`}]
                        });
                        const cleaned = raw.replace(/[*_\n]/g,"").trim();
                        if(cleaned) setSuppForm(f=>({...f,dose:cleaned}));
                      } catch(e){ if (process.env.NODE_ENV !== "production") { console.warn("[suppDose]",e); } }
                      setSuppLoading(false);
                    }} style={{background:T.aiTagBg,border:`1px solid ${T.aiColor}`,borderRadius:8,
                      padding:"10px 14px",cursor:"pointer",color:T.aiColor,
                      fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,
                      display:"flex",alignItems:"center",gap:4}}>
                      <Lightning size={12} weight="fill"/> AI Suggest
                    </button>
                  </div>
                  <p style={{color:T.disclaimerText||T.inkMuted,fontSize:9,fontFamily:T.fontDisplay,margin:"4px 0 6px",lineHeight:1.3}}>AI suggestion — consult a healthcare professional before use.</p>
                  <input value={suppForm.dose} onChange={e=>setSuppForm(f=>({...f,dose:e.target.value}))} placeholder="e.g. 1000mg, 1 capsule"
                    style={{width:"100%",background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:8,padding:"9px 10px",color:T.inkDark,fontSize:13,fontFamily:T.fontDisplay,fontWeight:600,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div style={{marginBottom:10}}>
                  <label style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,letterSpacing:"1px",display:"block",marginBottom:3}}>When to take</label>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {SUPP_TIMINGS.map(t=>(
                      <button key={t} onClick={()=>setSuppForm(f=>({...f,timing:t}))}
                        style={{padding:"6px 12px",background:suppForm.timing===t?T.accent:T.surfaceLight,
                          border:suppForm.timing===t?`2px solid ${T.accent}`:`1px solid ${T.cardBorder}`,
                          borderRadius:8,cursor:"pointer",fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,
                          color:suppForm.timing===t?"#fff":T.inkDark}}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{marginBottom:6}}>
                  <label style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,letterSpacing:"1px",display:"block",marginBottom:3}}>Notes (optional)</label>
                  <input value={suppForm.notes} onChange={e=>setSuppForm(f=>({...f,notes:e.target.value}))} placeholder="e.g. take with food"
                    style={{width:"100%",background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:8,padding:"9px 10px",color:T.inkDark,fontSize:12,fontFamily:T.fontDisplay,fontWeight:600,outline:"none",boxSizing:"border-box"}}/>
                </div>
              </div>
              <div style={{padding:"12px 16px",borderTop:`1px solid ${T.cardBorder}`,flexShrink:0}}>
                <button onClick={addSupplement} disabled={suppLoading||!suppForm.name.trim()} style={{
                  width:"100%",background:T.orange,border:"none",borderRadius:T.radiusSm,padding:14,
                  cursor:suppLoading||!suppForm.name.trim()?"default":"pointer",
                  color:"#fff",fontFamily:T.fontDisplay,fontSize:15,fontWeight:800,
                  display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                  opacity:suppLoading||!suppForm.name.trim()?0.5:1,
                }}>
                  {suppLoading?<><Spinner size={14}/> Saving...</>:<><Check size={14} weight="bold"/> Add to stack</>}
                </button>
              </div>
              </>
            )}
          </div>
        </div>
      )}


      <BottomNav screen={screen} setScreen={setScreen} hasTargets={hasTargets}/>
    </div></div>
  );
}
